// import "../../assets/css/style2.css";
import React, { useEffect, useState, useRef } from "react";
import { formatDate, formatDOB } from "../../helper";
import { useDispatch, useSelector } from "react-redux";
import { listPatient } from "../../actions/patientActions";
import { toast } from "react-toastify";
import ListHeading from "../list-record-components/list-header/listHeading";
import Table from "../react-table-component/ReactTable";
import { ArrowDownward, ArrowForwardIos } from "@mui/icons-material";
import { DropDownItem } from "../header/Header";
import historyIcon from "../../assets/img/svg/history-icon.svg";
import editIcon from "../../assets/img/svg/edit-icon.svg";
import dotMenuIcon from "../../assets/img/svg/dots-menu-icon.svg";
import PatientFilterCard from "./PatientFilterCard";
import "./patients.css";

const conditionMap = {
  1: "seizure",
  2: "concussion",
  3: "stroke",
  4: "Normal",
  5: "Altered Mental Status",
  0: "0",
};

export default function Patients({ category, study_type }) {
  const dispatch = useDispatch();
  const [selectedRows, setSelectedRows] = useState([]);
  const token = useSelector((state) => state.loginData.token);
  const patientList = useSelector((state) => state.patientList);
  const { loading, patients } = patientList;
  const [progress, setProgress] = useState(0);
  const [showFilter, setShowFilter] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState({});

  // CSV Download functionality
  const downloadCSV = () => {
    // Use the filtered patients so filters are applied to CSV
    const sourceData =
      filteredPatients && filteredPatients.length
        ? filteredPatients
        : patients || [];
    if (!sourceData || sourceData.length === 0) {
      toast.error("No data available to download.");
      return;
    }

    // Define CSV headers and data structure based on category
    let headers, csvData;

    if (category === "clinic") {
      headers = [
        "ID Number",
        "Name",
        "DOB",
        "Condition",
        "Last Visit",
        "First Visit",
        "Review Pending",
      ];

      csvData = sourceData.map((patient) => [
        patient?.id_number || "",
        patient?.name || "N/A",
        formatDOB(patient?.dob) || "",
        conditionMap[patient?.clinical_condition] || "",
        formatDate(patient?.recent_visits?.[0]?.timestamp) || "",
        formatDate(patient?.first_visit_date) || "",
        "Yes",
      ]);
    } else {
      headers = [
        "ID Number",
        "DOB",
        "Condition",
        "Last Visit",
        "First Visit",
        "Review Pending",
      ];

      csvData = sourceData.map((patient) => [
        patient?.id_number || "",
        formatDOB(patient?.dob) || "",
        conditionMap[patient?.clinical_condition] || "",
        formatDate(patient?.recent_visits?.[0]?.timestamp) || "",
        formatDate(patient?.first_visit_date) || "",
        "Yes",
      ]);
    }

    // Convert data rows to CSV format
    const csvRows = csvData.map((row) => row.join(","));

    // Combine headers and data
    const csvContent = [headers.join(","), ...csvRows].join("\n");

    // Create and download the file
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `patients_${new Date().toISOString().split("T")[0]}.csv`
    );
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    toast.success("CSV file downloaded successfully!");
  };

  useEffect(() => {
    const simulateProgress = () => {
      let interval = setInterval(() => {
        setProgress((prevProgress) =>
          prevProgress >= 100 ? 100 : prevProgress + 5
        );
      }, 500);
      return () => clearInterval(interval);
    };

    simulateProgress();

    dispatch(
      listPatient(token, {
        category,
        study_type,
      })
    )
      .then(() => {
        setProgress(100);
      })
      .catch(() => {
        setProgress(100);
      });
  }, [dispatch, token]);

  const [searchField, setSearchField] = useState("");

  const handleSearch = (e) => {
    const searchField = e.target.value.toLocaleLowerCase();
    setSearchField(searchField);
  };

  // Filter functionality
  const handleFilterClick = () => {
    setShowFilter((prev) => !prev);
  };

  const handleCloseFilter = () => {
    setShowFilter(false);
  };

  const handleApplyFilter = (filters) => {
    setAppliedFilters(filters || {});
    if (
      filters?.dateFrom ||
      filters?.dateTo ||
      filters?.condition ||
      filters?.reviewPending
    ) {
      toast.success("Filter applied successfully!");
    } else {
      toast.info("All filters cleared");
    }
  };

  const filteredPatients = React.useMemo(() => {
    if (!Array.isArray(patients)) return [];
    const searchValue = searchField?.toLowerCase() || "";

    return patients.filter((patient) => {
      // Search filter
      const patientName = patient?.name ? patient.name.toLowerCase() : "";
      const id_number = patient?.id_number
        ? patient?.id_number.toLowerCase()
        : "";

      const matchesSearch =
        patientName.includes(searchValue) || id_number.includes(searchValue);

      // Date filter
      let matchesDateFilter = true;
      if (appliedFilters.dateFrom || appliedFilters.dateTo) {
        let dateToCheck;

        if (appliedFilters.dateField === "dob") {
          dateToCheck = new Date(patient.dob);
        } else if (appliedFilters.dateField === "first_visit") {
          dateToCheck = new Date(patient.first_visit_date);
        } else {
          // Default to last visit
          dateToCheck = patient?.recent_visits?.[0]?.timestamp
            ? new Date(patient?.recent_visits?.[0]?.timestamp)
            : "";
        }

        const fromDate = appliedFilters.dateFrom
          ? new Date(appliedFilters.dateFrom)
          : null;
        const toDate = appliedFilters.dateTo
          ? new Date(appliedFilters.dateTo)
          : null;

        if (fromDate) {
          fromDate.setHours(0, 0, 0, 0);
        }
        if (toDate) {
          toDate.setHours(23, 59, 59, 999);
        }

        if (dateToCheck) {
          if (dateToCheck && (fromDate && dateToCheck) < fromDate) {
            matchesDateFilter = false;
          }
          if (dateToCheck && (toDate && dateToCheck) > toDate) {
            matchesDateFilter = false;
          }
        } else {
          matchesDateFilter = false;
        }
      }

      // Condition filter
      let matchesCondition = true;
      if (
        appliedFilters.condition !== undefined &&
        appliedFilters.condition !== null &&
        appliedFilters.condition !== ""
      ) {
        matchesCondition =
          Number(patient?.clinical_condition) ===
          Number(appliedFilters.condition);
      }

      // Review pending filter (always true for now since all patients show "Yes")
      let matchesReviewPending = true;
      if (
        appliedFilters.reviewPending !== undefined &&
        appliedFilters.reviewPending !== null &&
        appliedFilters.reviewPending !== ""
      ) {
        matchesReviewPending = appliedFilters.reviewPending === "true";
      }

      return (
        matchesSearch &&
        matchesDateFilter &&
        matchesCondition &&
        matchesReviewPending
      );
    });
  }, [patients, searchField, appliedFilters]);

  // const fetchData = useCallback(({ pageSize, pageIndex, params, sortBy }) => {
  //   const queryParams = {
  //     page: pageIndex + 1,
  //     // sortBy: sortBy[0]?.id || "createdAt",
  //     // sortOrder: sortBy[0]?.desc === false ? "asc" : "desc",
  //     // search: params,
  //   };
  //   dispatch(listPatient(token, queryParams));
  // }, []);

  const [expandedRows, setExpandedRows] = useState([]);

  const toggleRowExpansion = (rowIndex) => {
    setExpandedRows((prevExpandedRows) => {
      if (prevExpandedRows.includes(rowIndex)) {
        // Remove the row index if it's already expanded
        const newExpandedRows = prevExpandedRows.filter(
          (index) => index !== rowIndex
        );

        return newExpandedRows;
      } else {
        // Add the row index if it's not expanded
        const newExpandedRows = [...prevExpandedRows, rowIndex];
        return newExpandedRows;
      }
    });
  };

  const dotMenuRef = useRef(null);
  const [openMenuRowIndex, setOpenMenuRowIndex] = useState(null);
  const toggleMenuForRow = (rowIndex) => {
    setOpenMenuRowIndex((prevRowIndex) =>
      prevRowIndex === rowIndex ? null : rowIndex
    );
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dotMenuRef.current &&
        !dotMenuRef.current.contains(event.target) &&
        openMenuRowIndex !== null
      ) {
        setOpenMenuRowIndex(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [openMenuRowIndex]);

  const columns = React.useMemo(() => {
    let baseColumns = [
      {
        Header: "ID Number",
        accessor: "id_number",
        Cell: ({ row }) => {
          const isExpanded = expandedRows.includes(row.index);
          return (
            <div
              className="expansion-wrap"
              onClick={() => toggleRowExpansion(row.index)}
            >
              {isExpanded ? (
                <ArrowDownward style={{ fontSize: 12, marginRight: 4 }} />
              ) : (
                <ArrowForwardIos style={{ fontSize: 12, marginRight: 4 }} />
              )}
              {row?.original?.id_number ?? "N/A"}
            </div>
          );
        },
      },

      {
        Header: "DOB",
        accessor: "dob",
        Cell: ({ row }) => <div>{formatDOB(row.original.dob)}</div>,
      },
      {
        Header: "Condition",
        accessor: "condition",
        Cell: ({ row }) => (
          <div>{conditionMap[row.original.clinical_condition]}</div>
        ),
      },
      {
        Header: "Last Visit",
        accessor: "lastvisit",
        Cell: ({ row }) => (
          <div>
            {row.original?.recent_visits[0]
              ? formatDate(row.original?.recent_visits[0]?.timestamp)
              : "N/A"}
          </div>
        ),
      },
      {
        Header: "First Visit",
        accessor: "first_visit_date",
        Cell: ({ row }) => (
          <div>{formatDate(row.original.first_visit_date)}</div>
        ),
      },
      {
        Header: "Review Pending",
        accessor: "review",
        Cell: () => "Yes",
      },
      {
        Header: "",
        accessor: "action",
        disableSortBy: true,
        Cell: ({ row }) => {
          const isMenuOpen = openMenuRowIndex === row.index;
          return (
            <div className="dot-menu-icon">
              <img
                src={dotMenuIcon}
                alt="dot menu"
                onClick={() => toggleMenuForRow(row.index)}
              />

              {isMenuOpen && (
                <div ref={dotMenuRef} className="patients-dot-menu">
                  <DropDownItem
                    item={{
                      title: "View All Visits",
                      icon: historyIcon,
                      to: `/listrecordings/${row.original.id}`,
                    }}
                  />
                  <DropDownItem
                    item={{
                      title: "Edit Patient",
                      icon: editIcon,
                      to: `/edit-patient/${row.original.id}`,
                    }}
                  />
                </div>
              )}
            </div>
          );
        },
      },
    ];

    if (category === "clinic") {
      baseColumns.splice(1, 0, {
        Header: "Name",
        accessor: "name",
        Cell: ({ row }) => <div>{row.original.name ?? "N/A"}</div>,
      });
    }

    return baseColumns;
  }, [expandedRows, openMenuRowIndex, toggleRowExpansion]);

  const handleSelectedRowsChange = (selectedIds) => {
    setSelectedRows(selectedIds);
  };

  const [pillBtn, setPillBtn] = useState(null);

  return (
    <>
      <section className="patient-list">
        <ListHeading
          title="Patient Overview"
          handleSearch={handleSearch}
          onFilterClick={handleFilterClick}
          onDownloadClick={downloadCSV}
          hasActiveFilters={
            !!(
              appliedFilters.dateFrom ||
              appliedFilters.dateTo ||
              appliedFilters.condition ||
              appliedFilters.reviewPending
            )
          }
        />

        {showFilter && (
          <PatientFilterCard
            isOpen={showFilter}
            onClose={handleCloseFilter}
            onApplyFilter={handleApplyFilter}
            currentFilters={appliedFilters}
            conditionMap={conditionMap}
          />
        )}

        <Table
          columns={columns}
          data={filteredPatients ?? []}
          // params={searchField}
          // fetchData={fetchData}
          // pageCount={patients?.data?.pagination?.total_pages ?? 1}
          loading={loading}
          tableOf="Patients"
          progress={progress}
          onSelectedRowsChange={handleSelectedRowsChange}
        />
      </section>
    </>
  );
}
